import { useAppConfiguration } from "@/api/app-configuration.api";
import { useStopLists } from "@/api/stop-list.api";
import { getFilterGroupById } from "@/data/filters/config";
import { useTargetGroup } from "@/providers/TargetGroupProvider";
import { getFilterGroupsFromConfigurations } from "@/services/configurations.service";
import { formatContactsSidebarLabel } from "@/services/contacts.service";
import {
  Campaign,
  Configuration,
  ContactConfiguration,
} from "@/types/campaign";
import { AnimatePresence, motion, stagger, Variants } from "framer-motion";
import { X } from "lucide-react";
import { useMemo } from "react";
import { Button as RACButton } from "react-aria-components";
import { Button } from "../generic/Button";
import { PageHeader } from "../layout/PageHeader";

const MotionRACButton = motion.create(RACButton);

type TargetGroupSidebarProps = {
  configurations: Configuration[];
  stopLists: Campaign["stopLists"];
  onFilterGroupSelect?: (filterGroupId: string, category: string) => void;
  onStopListClick?: () => void;
};

export function TargetGroupSidebar({
  configurations,
  stopLists,
  onFilterGroupSelect,
  onStopListClick,
}: TargetGroupSidebarProps) {
  const { isSidebarOpen, hideSidebar } = useTargetGroup();
  const { data: stopListsQueryData } = useStopLists();

  const mainConfiguration = configurations.find((c) => c.quality === 1);

  const contactConfiguration = useMemo(() => {
    if (mainConfiguration?.contactConfiguration)
      return mainConfiguration.contactConfiguration;
    return {
      personas: [],
      requireContact: false,
    } satisfies ContactConfiguration;
  }, [mainConfiguration]);
  const hasContactConfiguration = useMemo(
    () => (mainConfiguration?.contactConfiguration?.personas?.length ?? 0) > 0,
    [mainConfiguration],
  );

  const filterGroups = getFilterGroupsFromConfigurations(configurations);

  const hasActiveStopLists = useMemo(() => {
    return stopLists?.length || stopListsQueryData?.some((s) => s.mandatory);
  }, [stopLists, stopListsQueryData]);

  const backdropVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 0.32,
      transition: {
        duration: 0.5,
        // The M3 Emphasized easing curve
        ease: [0.05, 0.7, 0.1, 1.0],
      },
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.2, // Exit transitions are typically shorter (Short 4)
        ease: [0.3, 0, 1, 1], // Emphasized Accelerated
      },
    },
  };

  const sidebarVariants: Variants = {
    hidden: {
      x: "100%",
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40,
      },
    },
    visible: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        restDelta: 0.001, // Ensure it comes to a complete stop
        // This is the magic for staggered children:
        delayChildren: stagger(0.1, {
          startDelay: 0.2,
        }),
      },
    },
  };

  return (
    <>
      <AnimatePresence>
        {isSidebarOpen && (
          <div className="fixed inset-0 z-50 size-full overflow-hidden">
            <motion.div
              key="target-group-sidebar-backdrop"
              className="absolute inset-0 rounded-xl bg-black"
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={() => {
                hideSidebar();
              }}
            />

            <motion.aside
              key="target-group-sidebar"
              className="bg-theme-background absolute top-0 right-0 z-51 h-full w-xs overflow-y-auto rounded-r-xl px-8"
              variants={sidebarVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              <PageHeader
                className="px-0 xl:px-0"
                title="Filtre"
                sticky={false}
                actions={
                  <Button
                    className="-mr-2 min-w-auto p-2"
                    intent="tertiary"
                    size="rounded"
                    onPress={hideSidebar}
                  >
                    <X size={20} />
                  </Button>
                }
              />

              <div className="-m-4 flex flex-col gap-4 py-12">
                <TGSButton
                  key="stop-lists"
                  title="Stoplister"
                  description={hasActiveStopLists ? "Aktiveret" : "Deaktiveret"}
                  onPress={() => {
                    hideSidebar();
                    onStopListClick?.();
                  }}
                />
                {/*<Separator className="my-2" />*/}
                {hasContactConfiguration && (
                  <TGSButton
                    key="contact-filter"
                    title="Kontaktpersoner"
                    description={
                      contactConfiguration.personas?.length
                        ? formatContactsSidebarLabel(contactConfiguration)
                        : "Ingen kontaktpersoner"
                    }
                    onPress={() => {
                      onFilterGroupSelect?.("contacts", "contacts");
                      hideSidebar();
                    }}
                  />
                )}
                {Array.from(filterGroups.keys()).map((filterGroupId) => {
                  const filterGroup = getFilterGroupById(filterGroupId);
                  if (!filterGroup) return null;

                  const { group, category } = filterGroup;

                  return (
                    <TGSButton
                      key={filterGroupId}
                      title={group.title}
                      description={
                        <group.DisplayValue
                          filters={mainConfiguration?.query.filters ?? []}
                        />
                      }
                      onPress={() => {
                        hideSidebar();
                        // Navigate to the filter group details page
                        onFilterGroupSelect?.(filterGroupId, category.id);
                      }}
                    />
                  );
                })}
              </div>
            </motion.aside>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}

function TGSButton({
  title,
  description,
  onPress,
}: {
  title: string;
  description: string | React.ReactNode;
  onPress?: () => void;
}) {
  const itemVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 10,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 30,
        duration: 0.2,
      },
    },
  };

  return (
    <MotionRACButton
      variants={itemVariants}
      className="hover:bg-theme-on-glass-hover flex cursor-pointer flex-col items-start justify-start gap-1 rounded bg-transparent p-4 transition-colors duration-150"
      onPress={onPress}
    >
      <h3 className="text-theme-on-background text-left text-xs font-semibold">
        {title}
      </h3>
      <p className="text-theme-on-background-variant text-left text-xs">
        {description}
      </p>
    </MotionRACButton>
  );
}
